import { branding } from '@fsd/shared';
import { Button, WhatsAppIcon } from '@fsd/ui';
import { Minus, Plus, ShoppingBag, Trash2, X } from 'lucide-react';
import { useTranslation } from '@/i18n/hooks';
import { useCart } from '@/utils';

export function CartSheet() {
  const { t, locale } = useTranslation('cart');
  const { items, totalItems, isOpen, setIsOpen, updateQuantity, removeItem } = useCart();

  const handleCheckout = () => {
    if (items.length === 0) return;

    const message = generateWhatsAppMessage(items, locale);
    const whatsappUrl = branding.social.whatsapp;
    const url = whatsappUrl.includes('?')
      ? `${whatsappUrl}&text=${encodeURIComponent(message)}`
      : `${whatsappUrl}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
    setIsOpen(false);
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-60 bg-black/50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={() => setIsOpen(false)}
        aria-hidden={!isOpen}
      />
      <div
        className={`fixed right-0 top-0 z-60 flex h-full w-full max-w-md flex-col bg-white shadow-xl transition-transform duration-300 ease-in-out dark:bg-slate-900 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center justify-between border-b border-slate-200 px-6 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('title')} ({totalItems})
          </h2>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
            aria-label={t('close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                <ShoppingBag className="h-10 w-10 text-slate-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('empty')}
              </h3>
              <p className="mb-6 text-slate-600 dark:text-slate-400">{t('emptyDescription')}</p>
              <Button onClick={() => setIsOpen(false)} variant="outline">
                {t('continueShopping')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => {
                const name = item.drone.name[locale];

                return (
                  <div
                    key={`${item.id}-${item.isUsed}`}
                    className="flex gap-4 rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                  >
                    <img
                      src={item.drone.image}
                      alt={name}
                      className="h-20 w-20 shrink-0 rounded-lg object-cover"
                    />
                    <div className="flex flex-1 flex-col gap-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {name}
                          </h3>
                          {item.isUsed && item.drone.condition && (
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {item.drone.condition[locale]}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.id, item.isUsed)}
                          className="rounded-lg p-1 text-slate-400 transition-colors hover:text-red-600 dark:hover:text-red-400"
                          aria-label={t('removeItem')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.id, item.isUsed, item.quantity - 1)}
                            className="rounded-lg border border-slate-300 p-1 text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-800"
                            aria-label={t('decreaseQuantity')}
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-8 text-center text-sm font-medium text-slate-900 dark:text-slate-100">
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.id, item.isUsed, item.quantity + 1)}
                            className="rounded-lg border border-slate-300 p-1 text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-800"
                            aria-label={t('increaseQuantity')}
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-slate-200 bg-slate-50 p-6 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex gap-3">
              <Button
                onClick={handleCheckout}
                className="flex-1 bg-[#25D366] text-white shadow-lg transition-all hover:bg-[#20BA5A] hover:shadow-xl"
              >
                <WhatsAppIcon className="mr-2 h-5 w-5" />
                {t('checkout')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function generateWhatsAppMessage(
  items: Array<{
    drone: { name: Record<string, string> };
    quantity: number;
    isUsed: boolean;
  }>,
  locale: 'pt-BR' | 'en-US' | 'es-ES'
): string {
  const messages = {
    'pt-BR': {
      header: 'Olá! Gostaria de informações sobre:\n\n',
      item: (name: string, quantity: number) => `• ${name} - Qtd: ${quantity}\n`,
    },
    'en-US': {
      header: 'Hello! I would like information about:\n\n',
      item: (name: string, quantity: number) => `• ${name} - Qty: ${quantity}\n`,
    },
    'es-ES': {
      header: '¡Hola! Me gustaría información sobre:\n\n',
      item: (name: string, quantity: number) => `• ${name} - Cant: ${quantity}\n`,
    },
  };

  const t = messages[locale];
  let message = t.header;

  items.forEach((item) => {
    const name = item.drone.name[locale];
    if (!name) return;
    message += t.item(name, item.quantity);
  });

  return message;
}
